import React, { useMemo, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';

import { useGetBoostedCatalog } from '~/hooks/apis/boostedCatalog/useGetBoostedCatalog';

import type { BoostKind } from '~/services/BoostedCatalogService';
import { formatDateShortDD, formatDateShortWithClock } from '~/services/TimeService';

import { downloadCsvFile } from '~/utils/csvDownload';

import HowBoostingWorks from './HowBoostingWorks';
import HowToRead from './HowToRead';
import {
  BOOST_KINDS,
  BOOST_KIND_LABELS,
  type CatalogFilters,
  type CatalogRow,
  type WindowStatus,
  countByKind,
  filterRows,
  formatBoostValue,
  toRows,
} from './boostedCatalogRows';

const KIND_DESCRIPTIONS: Record<BoostKind, string> = {
  lpp_boost: 'Bedbank hotels in the LPP pool. Score is quality minus 7.5.',
  lpc_discount: 'LPC hotels whose extranet discount started selling in the last 7 days. Value is the best discount.',
  new_lpc: 'LPCs in their first 7 days. Value is the days left.',
};

const CSV_HEADERS = [
  'boost',
  'name',
  'place',
  'value',
  'startDate',
  'endDate',
  'window',
  'impressions7d',
  'lifetimeImpressions',
  'sales7d',
  'offerId',
];

const NO_VALUE = '—';

function dateColumn(field: 'startDate' | 'endDate', headerName: string): GridColDef<CatalogRow> {
  return {
    field,
    headerName,
    type: 'date',
    width: 120,
    valueGetter: (value: string | null) => (value ? new Date(value) : null),
    valueFormatter: (value: Date | null) => (value ? formatDateShortDD(value) : NO_VALUE),
  };
}

function countColumn(
  field: 'impressions7d' | 'lifetimeImpressions' | 'sales7d',
  headerName: string,
  width: number,
): GridColDef<CatalogRow> {
  return {
    field,
    headerName,
    type: 'number',
    width,
    valueFormatter: (value: number) => value.toLocaleString(),
  };
}

const COLUMNS: Array<GridColDef<CatalogRow>> = [
  { field: 'name', headerName: 'Hotel', flex: 2, minWidth: 220 },
  { field: 'place', headerName: 'Place', flex: 2, minWidth: 240 },
  {
    field: 'kind',
    headerName: 'Boost',
    width: 120,
    valueFormatter: (value: BoostKind) => BOOST_KIND_LABELS[value],
  },
  {
    field: 'value',
    headerName: 'Value',
    type: 'number',
    width: 120,
    valueFormatter: (value: number, row: CatalogRow) => formatBoostValue(row.kind, value),
  },
  dateColumn('startDate', 'Start'),
  dateColumn('endDate', 'End'),
  {
    field: 'windowStatus',
    headerName: 'Window',
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<CatalogRow, WindowStatus>) => {
      if (value === 'ended') {
        return <Chip size="small" color="warning" label="Ended" />;
      }
      if (value === 'active') {
        return <Chip size="small" variant="outlined" label="In window" />;
      }
      return NO_VALUE;
    },
  },
  countColumn('impressions7d', 'Impressions 7d', 140),
  countColumn('lifetimeImpressions', 'Lifetime impressions', 170),
  countColumn('sales7d', 'Sales 7d', 100),
  { field: 'offerId', headerName: 'Offer ID', flex: 1.5, minWidth: 240 },
];

export default function BoostedCatalogPage() {
  const [filters, setFilters] = useState<CatalogFilters>({ kind: 'all', search: '' });

  const { data, isLoading, isFetching, error, refetch } = useGetBoostedCatalog();

  const rows = useMemo(() => toRows(data?.entries ?? [], new Date()), [data]);
  const kindCounts = useMemo(() => countByKind(rows), [rows]);
  const endedCounts = useMemo(() => countByKind(rows.filter((row) => row.windowStatus === 'ended')), [rows]);
  const visibleRows = useMemo(() => filterRows(rows, filters), [rows, filters]);

  const handleDownload = () => {
    downloadCsvFile(
      visibleRows.map((row) => ({
        boost: BOOST_KIND_LABELS[row.kind],
        name: row.name,
        place: row.place,
        value: formatBoostValue(row.kind, row.value),
        startDate: row.startDate,
        endDate: row.endDate,
        window: row.windowStatus,
        impressions7d: row.impressions7d,
        lifetimeImpressions: row.lifetimeImpressions,
        sales7d: row.sales7d,
        offerId: row.offerId,
      })),
      CSV_HEADERS,
      `boosted-catalog-${new Date().toISOString().slice(0, 10)}.csv`,
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Hotels currently flagged for a search boost, read live from the search index.
      </Typography>

      <Stack spacing={1}>
        <HowBoostingWorks />
        <HowToRead />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={isFetching}>
          Refresh
        </Button>
        <Button startIcon={<DownloadIcon />} onClick={handleDownload} disabled={visibleRows.length === 0}>
          Download CSV
        </Button>
        {data && (
          <Typography variant="caption" color="text.secondary">
            Snapshot taken {formatDateShortWithClock(data.generatedAt)}
          </Typography>
        )}
      </Stack>

      {error && <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load the catalog'}</Alert>}

      {isLoading && (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography variant="body2">Reading the search index. The first load takes about 10 seconds.</Typography>
        </Stack>
      )}

      {data && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {BOOST_KINDS.map((kind) => (
              <Card
                key={kind}
                variant="outlined"
                sx={{ flex: 1, borderColor: filters.kind === kind ? 'primary.main' : undefined }}
              >
                <CardActionArea
                  onClick={() => setFilters((current) => ({ ...current, kind: current.kind === kind ? 'all' : kind }))}
                >
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {BOOST_KIND_LABELS[kind]}
                    </Typography>
                    <Typography variant="h4">{kindCounts[kind].toLocaleString()}</Typography>
                    {endedCounts[kind] > 0 && (
                      <Typography variant="body2" color="warning.main">
                        {endedCounts[kind].toLocaleString()} past their window
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {KIND_DESCRIPTIONS[kind]}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                size="small"
                label="Search hotel, place or offer ID"
                value={filters.search}
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                select
                size="small"
                label="Boost"
                value={filters.kind}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, kind: event.target.value as CatalogFilters['kind'] }))
                }
                sx={{ width: 180 }}
              >
                <MenuItem value="all">All boosts</MenuItem>
                {BOOST_KINDS.map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {BOOST_KIND_LABELS[kind]}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Showing {visibleRows.length.toLocaleString()} of {rows.length.toLocaleString()} rows
            </Typography>

            <DataGrid
              rows={visibleRows}
              columns={COLUMNS}
              density="compact"
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
}
